/**
 * Vorlage „Anrichten“.
 *
 * Auf einen Teller kommt mit dem Löffel die Beilage, daneben das Fleisch,
 * darüber ein paar Tropfen Soße und zum Schluss ein Kräuterblatt.
 */
import { ease, lerp, segment } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { path } from '../paths.ts';
import { circle, compact, faded, group, rotated, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { meatPiece } from '../parts/food.ts';
import { plate } from '../parts/kitchen.ts';
import { arrow, counter, doneMark, GROUND_Y, shadow } from '../parts/stage.ts';
import { place, woodenSpoon } from '../parts/utensils.ts';
import { defineTemplate } from '../template.ts';

/** Teller. */
const PLATE = { cx: 112, y: GROUND_Y - 10, width: 170 } as const;

/** Zeitplan. */
const TIME = {
  spoon: [0.04, 0.26],
  mound: [0.12, 0.24],
  meat: [0.3, 0.44],
  sauce: [0.48, 0.62],
  leaf: [0.64, 0.74],
  done: [0.76, 0.8],
  vanish: [0.9, 0.98],
} as const;

/** Beilage als weicher Hügel. */
function mound(amount: number): SceneNode | null {
  if (amount <= 0.01) {
    return null;
  }
  const height = 22 * amount;
  const width = 30 + 14 * amount;
  const cx = PLATE.cx - 22;
  const d = path()
    .move(cx - width, PLATE.y + 1)
    .curve(
      cx - width * 0.6,
      PLATE.y - height * 1.3,
      cx + width * 0.6,
      PLATE.y - height * 1.3,
      cx + width,
      PLATE.y + 1,
    )
    .close()
    .toString();
  return group([
    shape(d, styles.snow()),
    shape(
      path()
        .move(cx - width * 0.4, PLATE.y - height * 0.5)
        .quad(cx, PLATE.y - height * 0.9, cx + width * 0.4, PLATE.y - height * 0.55)
        .toString(),
      {
        stroke: 'shade',
        strokeWidth: STROKE.fine,
      },
    ),
  ]);
}

/** Vorlage „Anrichten“. */
export const prepPlateTemplate = defineTemplate({
  id: 'prep.plate',
  group: 'prep',
  title: 'Anrichten',
  description:
    'Die Beilage wird mit dem Löffel auf den Teller gesetzt, das Fleisch angelegt, mit etwas Soße beträufelt und mit Kräutern garniert.',
  durationSeconds: 6,
  keywords: [
    'anrichten',
    'servieren',
    'auf tellern',
    'garnieren',
    'auf einem teller',
    'portionieren',
    'teller',
  ],
  stillTime: 0.78,
  parameters: [],
  scene(_values, t) {
    const visible = 1 - ease.inCubic(segment(t, ...TIME.vanish));
    const spoon = segment(t, ...TIME.spoon);
    const spoonOn = Math.sin(spoon * Math.PI);
    const amount = ease.outQuad(segment(t, ...TIME.mound));
    const meat = ease.outCubic(segment(t, ...TIME.meat));
    const sauce = segment(t, ...TIME.sauce);
    const leaf = ease.outQuad(segment(t, ...TIME.leaf));
    const done = segment(t, ...TIME.done) * (1 - segment(t, ...TIME.vanish));

    const drops: SceneNode[] = [0, 1, 2, 3]
      .filter((index) => sauce > (index + 0.5) / 4)
      .map((index) =>
        circle(PLATE.cx + 6 + index * 12, PLATE.y - 2 + (index % 2) * 2, 2.6, styles.flat('deep')),
      );
    const leafY = lerp(20, PLATE.y - 24, leaf);
    const leafNode =
      leaf > 0
        ? rotated(lerp(-50, -15, leaf), PLATE.cx - 22, leafY, [
            shape(
              path()
                .move(PLATE.cx - 32, leafY)
                .quad(PLATE.cx - 22, leafY - 8, PLATE.cx - 12, leafY)
                .quad(PLATE.cx - 22, leafY + 8, PLATE.cx - 32, leafY)
                .close()
                .toString(),
              {
                fill: 'shade',
                stroke: 'ink',
                strokeWidth: STROKE.detail,
              },
            ),
            shape(
              path()
                .move(PLATE.cx - 30, leafY)
                .line(PLATE.cx - 14, leafY)
                .toString(),
              styles.fine('deep'),
            ),
          ])
        : null;
    const meatNode =
      meat > 0
        ? group([meatPiece(PLATE.cx + 26, PLATE.y - 9, 50, 16, 0.6, 0.4)], {
            transform: { x: lerp(80, 0, meat), rotate: lerp(10, 0, meat), ox: PLATE.cx + 26, oy: PLATE.y },
          })
        : null;
    return compact([
      ...counter(),
      shadow(PLATE.cx, PLATE.width * 0.4),
      ...plate(PLATE.cx, PLATE.y, PLATE.width),
      amount > 0.01 ? faded(visible, [mound(amount), meatNode, ...drops, leafNode]) : null,
      faded(spoonOn, [
        place(
          lerp(PLATE.cx + 10, PLATE.cx - 22, ease.inOutSine(spoon)),
          PLATE.y - 10 - amount * 20,
          40,
          woodenSpoon(),
          0.85,
        ),
      ]),
      faded(ease.outBack(done), [doneMark(204, 40, 1.2)]),
    ]);
  },
  hints() {
    return [
      arrow([
        { x: 200, y: 112 },
        { x: 170, y: 118 },
      ]),
    ];
  },
});
